import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { baseModelShape } from '../base-model'
import { userShape } from '../user/models'

// Native enums
export const messageDirectionEnum = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
} as const

export type MessageDirectionValues =
  (typeof messageDirectionEnum)[keyof typeof messageDirectionEnum]

export const messageSenderEnum = {
  CLIENT: 'client',
  ADMIN: 'admin',
} as const

export type MessageSenderValues = (typeof messageSenderEnum)[keyof typeof messageSenderEnum]

export const messageStatusEnum = {
  PENDING: 'pending',
  SENDING: 'sending',
  QUEUED: 'queued',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed',
} as const

export type MessageStatusValues = (typeof messageStatusEnum)[keyof typeof messageStatusEnum]

// The property fields a template variable can bind to (property source).
export const propertyRefShape = {
  name: z.string().optional().nullable(),
  formattedAddress: z.string().optional().nullable(),
  street: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  stateOrRegion: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
}

// Reservation reference shape (lightweight)
export const reservationRefShape = {
  id: z.string().uuid(),
  checkinDate: z.string().optional().nullable(),
  checkoutDate: z.string().optional().nullable(),
  providerConfirmationCode: z.string().optional().nullable(),
  provider: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  propertyRef: z.object(propertyRefShape).optional().nullable(),
  // Computed binding fields (check-in date/time...) resolved server-side per
  // format language (`en`, `es`), e.g. `{ en: { checkinTime: '3:00 PM' } }`.
  localizedFields: z.record(z.string(), z.record(z.string(), z.string())).optional().nullable(),
  // The guest check-in survey link; null unless the property reports TRA/SIRE.
  checkinSurveyLink: z.string().optional().nullable(),
}

// Guest reference shape (lightweight)
export const guestRefShape = {
  id: z.string().uuid(),
  firstName: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
}

// Contact reference shape (lightweight)
export const contactRefShape = {
  id: z.string().uuid(),
  displayName: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  language: z.string().optional().nullable(),
}

// Full org contact shape (directory CRUD + picking a recipient)
export const contactShape = {
  ...baseModelShape,
  organization: z.string().uuid(),
  displayName: z.string(),
  phoneNumber: z.string(),
  email: z.email().optional().nullable(),
  language: z.string().optional().nullable(),
}

export const contactFilterShape = {
  organization: z.string(),
  search: z.string(),
}

// Template shapes — the composer reads approved rows; `isDefault` marks the
// platform's first-contact template so the client never hard-codes its name.
export const templateStatusEnum = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  DISABLED: 'disabled',
} as const

export type TemplateStatusValue = (typeof templateStatusEnum)[keyof typeof templateStatusEnum]

// WhatsApp template categories Meta recognises. The default first-contact
// template is `utility`; org admins pick one when authoring a custom template.
export const templateCategoryEnum = {
  UTILITY: 'utility',
  MARKETING: 'marketing',
  AUTHENTICATION: 'authentication',
} as const

export type TemplateCategoryValue = (typeof templateCategoryEnum)[keyof typeof templateCategoryEnum]

// Permission codename gating template authoring/submitting/disabling — the same
// gate the backend viewset enforces. Nav visibility, the route guard, and the
// management page all key off this so admins and only admins reach the feature.
export const WHATSAPP_TEMPLATES_MANAGE_PERMISSION = 'whatsapp_templates.manage'

// App languages a template can be authored in — matches client/src/i18n/locale/.
export const templateLanguageOptions = ['en', 'es'] as const

// Where a `{{variable}}` gets its value from at send time. A `manual` binding is
// typed by the sender in the composer; the rest resolve from the conversation's
// linked entity. Mirrors whatsapp/bindings.py on the server.
export const bindingSourceEnum = {
  CONTACT: 'contact',
  RESERVATION: 'reservation',
  GUEST: 'guest',
  ORGANIZATION: 'organization',
  RESIDENCE: 'residence',
  MANUAL: 'manual',
} as const

export type BindingSourceValue = (typeof bindingSourceEnum)[keyof typeof bindingSourceEnum]

// Fields exposed per entity source — the single source of truth the authoring
// pickers key off. Kept in snake_case to match the API/DB field names exactly.
export const bindingSourceFields: Record<Exclude<BindingSourceValue, 'manual'>, string[]> = {
  [bindingSourceEnum.CONTACT]: ['display_name', 'phone_number', 'email', 'language'],
  [bindingSourceEnum.RESERVATION]: [
    'checkin_date',
    'checkin_time',
    'checkout_date',
    'checkout_time',
    'checkin_survey_link',
    'provider_confirmation_code',
    'provider',
    'status',
  ],
  [bindingSourceEnum.GUEST]: ['first_name', 'last_name', 'phone_number', 'email'],
  [bindingSourceEnum.ORGANIZATION]: ['name'],
  [bindingSourceEnum.RESIDENCE]: [
    'name',
    'formatted_address',
    'street',
    'city',
    'state_or_region',
    'postal_code',
  ],
}

// Stored as a list (not a dict keyed by variable name) so tn-models' key
// camelCasing never mangles authored variable names like `customer_name`.
export const templateVariableBindingShape = {
  variable: z.string(),
  source: z.string(),
  field: z.string().optional().nullable(),
}

export type TemplateVariableBinding = GetInferredFromRaw<typeof templateVariableBindingShape>

export const whatsappTemplateShape = {
  ...baseModelShape,
  organization: z.string().uuid(),
  name: z.string(),
  category: z.string(),
  language: z.string(),
  body: z.string(),
  variableBindings: z.array(z.object(templateVariableBindingShape)).optional().default([]),
  status: z.enum(templateStatusEnum),
  isDefault: z.boolean(),
  // Populated by the approval sync when Meta rejects/errors a submission, so the
  // management page can tell an admin why a template is stuck.
  rejectionReason: z.string().optional().nullable(),
  submissionError: z.string().optional().nullable(),
}

// On-demand approval check response: the refreshed template row plus whether
// the check actually changed its status — lets the UI distinguish "now
// resolved" from "still pending" instead of looking like it did nothing.
export const templateCheckStatusResultShape = {
  ...whatsappTemplateShape,
  changed: z.boolean(),
}

export type TemplateCheckStatusResult = GetInferredFromRaw<typeof templateCheckStatusResultShape>

// Authoring a custom template: the create call also submits it to Meta for
// approval in the same request (see the backend viewset), so a created row comes
// back `pending` and its status refreshes as the approval sync runs.
export const createWhatsappTemplateShape = {
  organization: z.string().uuid(),
  name: whatsappTemplateShape.name,
  category: whatsappTemplateShape.category,
  language: whatsappTemplateShape.language,
  body: whatsappTemplateShape.body,
  variableBindings: z.array(z.object(templateVariableBindingShape)),
  // `false` saves a `draft` (only name + language validated); omitted or
  // `true` submits to Meta right away.
  submit: z.boolean().optional(),
}

// A ready-made template the server offers an org (today: the TRA/SIRE check-in
// registration message, only for orgs with a reporting property). Read from
// the list response's top-level `recommended_templates`, which tn-models leaves
// snake_case — hence `variable_bindings`.
export const recommendedTemplateShape = {
  name: z.string(),
  category: z.string(),
  language: z.string(),
  body: z.string(),
  variable_bindings: z.array(z.object(templateVariableBindingShape)),
}

export type RecommendedTemplate = GetInferredFromRaw<typeof recommendedTemplateShape>

export const whatsappTemplateFilterShape = {
  organization: z.string(),
  status: z.string(),
  language: z.string(),
  name: z.string(),
  includeDisabled: z.boolean(),
}

// Conversation shapes
export const conversationCreateShape = {
  ourNumber: z.string().optional(),
  contactNumber: z.string(),
  reservation: z.string().uuid().optional().nullable(),
  guest: z.string().uuid().optional().nullable(),
  // Optional org-contact enrichment when starting a conversation by number:
  // name + language are applied to the created/linked Contact in the same step.
  contactName: z.string().optional(),
  contactLanguage: z.string().optional(),
}

export const conversationShape = {
  ...baseModelShape,
  user: z.string().uuid(),
  organization: z.string().uuid().optional().nullable(),
  ourNumber: z.string(),
  contactNumber: z.string(),
  lastMessageAt: z.string().datetime().optional().nullable(),
  lastClientMessageAt: z.string().datetime().optional().nullable(),
  // WhatsApp's 24h customer-service window, derived server-side from
  // lastClientMessageAt. `windowExpiresAt` is null until the customer has
  // written, and first contact is always window-closed (template required).
  windowExpiresAt: z.string().datetime().optional().nullable(),
  isWindowOpen: z.boolean().optional().default(false),
  lastMessagePreview: z.string().optional().nullable(),
  unreadClientCount: z.number().int().optional().default(0),
  reservation: z.string().uuid().optional().nullable(),
  reservationRef: z.object(reservationRefShape).optional().nullable(),
  guest: z.string().uuid().optional().nullable(),
  guestRef: z.object(guestRefShape).optional().nullable(),
  contact: z.string().uuid().optional().nullable(),
  contactRef: z.object(contactRefShape).optional().nullable(),
}

export const conversationFilterShape = {
  search: z.string(),
  // Scope the list to the org chosen in the chat selector. Still bounded
  // server-side to the user's own orgs.
  organization: z.string().uuid(),
}

// Message shapes
export const messageCreateShape = {
  conversation: z.string().uuid(),
  // Free-form text, allowed only while the 24h window is open. Omitted for a
  // business-initiated send, which names an approved template instead.
  content: z.string().optional(),
  mediaUrls: z.array(z.string()).optional(),
  templateName: z.string().optional(),
  // Values for the template's {{named}} variables, and the approved language to
  // send it in (defaults server-side to the contact's language).
  templateVariables: z.record(z.string(), z.string()).optional(),
  templateLanguage: z.string().optional(),
}

// Media attachment shape (stored per-message as JSON)
export const mediaItemShape = {
  url: z.string(),
  contentType: z.string(),
  filename: z.string(),
}

export type MediaItem = GetInferredFromRaw<typeof mediaItemShape>

export const messageShape = {
  ...baseModelShape,
  content: z.string(),
  status: z.enum(messageStatusEnum),
  sender: z.enum(messageSenderEnum),
  direction: z.enum(messageDirectionEnum),
  conversation: z.string().uuid(),
  user: z.object(userShape).optional().nullable(), // Legacy field
  sentByUser: z.object(userShape).optional().nullable(), // User who sent the message
  seenByUsers: z.array(z.object(userShape)).optional().nullable().default([]), // Users who have seen this message
  media: z.array(z.object(mediaItemShape)).optional().nullable(), // Media attachments
}

export const messageFilterShape = {
  conversation: z.string(),
  search: z.string(),
}

// Link reservation request shape
export const linkReservationRequestShape = {
  reservationId: z.string().uuid(),
}

// Contact search result shape
export const contactSearchResultShape = {
  guest: z.object(guestRefShape),
  reservation: z.object(reservationRefShape).nullable().optional(),
}

// Types
export type Conversation = GetInferredFromRaw<typeof conversationShape>
export type ConversationCreate = GetInferredFromRaw<typeof conversationCreateShape>
export type ConversationFilter = GetInferredFromRaw<typeof conversationFilterShape>
export type Message = GetInferredFromRaw<typeof messageShape>
export type MessageCreate = GetInferredFromRaw<typeof messageCreateShape>
export type MessageFilter = GetInferredFromRaw<typeof messageFilterShape>
export type ReservationRef = GetInferredFromRaw<typeof reservationRefShape>
export type GuestRef = GetInferredFromRaw<typeof guestRefShape>
export type ContactRef = GetInferredFromRaw<typeof contactRefShape>
export type LinkReservationRequest = GetInferredFromRaw<typeof linkReservationRequestShape>
export type ContactSearchResult = GetInferredFromRaw<typeof contactSearchResultShape>
export type Contact = GetInferredFromRaw<typeof contactShape>
export type ContactFilter = GetInferredFromRaw<typeof contactFilterShape>
export type WhatsappTemplate = GetInferredFromRaw<typeof whatsappTemplateShape>
export type CreateWhatsappTemplate = GetInferredFromRaw<typeof createWhatsappTemplateShape>
export type WhatsappTemplateFilter = GetInferredFromRaw<typeof whatsappTemplateFilterShape>
